/** mobx */
import { action, computed, makeAutoObservable, observable, toJS, transaction } from "mobx";
import { useLocalObservable } from "mobx-react-lite";
/** 3rd party */
import posthog from "posthog-js";
import { createContext, useContext, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { v4 } from "uuid";

/** types */
import type { Variable } from "@/components/InputVariableConfigurator/types";
import type { IMessageStore } from "@/components/Playground/Messages/Message/message-store";
import type {
  Message,
  ModelParameters,
  Operation,
  PlaygroundDrawerModeType,
  PlaygroundSessionModeType,
  PlaygroundType,
  RunGroupRequest,
  RunGroupResponse,
  TemplateFormat,
  VariableSet,
} from "@/components/Playground/types";
import type {
  ChatSettingsI,
  PlaygroundSession,
  PlaygroundSessionSource,
} from "@/queries/playground/useUpdatePlaygroundSession";
import type {
  Metadata,
  Model,
  PromptBlueprint,
  Tool,
  ToolChoice,
  BuiltInToolProvider,
} from "@/types";
import type { PromptVersion } from "@/types/apiGetters";
import type { IndividualRun } from "@/types/metadata";
import type { InputVariableRow } from "@/types/playground";
import type {
  BuiltInTool,
  ChatFunctionCall,
  CustomFunction_,
  Function_,
  FunctionTool,
  PromptLayerMcpTool,
  RegistryTool,
  SchemaDefinition,
  ToolVariable,
} from "@/types/prompt-blueprint";
/** stores */
import SnippetOverrideStore from "@/components/ContentArea/snippet-override-store";
import { MessageStore } from "@/components/Playground/Messages/Message/message-store";
import ChatStore from "@/components/Playground/chat-store";
import OperationStore from "@/components/Playground/operation-store";
/** constants */
import {
  DEFAULT_CHAT_MODEL,
  DEFAULT_COMPLETION_MODEL,
  DEFAULT_MESSAGE_1,
  DEFAULT_MESSAGE_1_ROLE,
  DEFAULT_MESSAGE_2,
  DEFAULT_MESSAGE_2_ROLE,
  DEFAULT_MESSAGE_TYPE,
  DEFAULT_MODEL,
  PLAYGROUND_DRAWER_MODES,
  PLAYGROUND_SESSION_MODES,
  PLAYGROUND_TYPES,
} from "@/components/Playground/types";
import { getFinalMessage, parseTypeOptimistically } from "@/components/Playground/utils";
/** api */
import { ENDPOINTS } from "@/api/application-api";
/** context/hooks */
import { useAuth } from "@/context/auth-context";
/** enums */
import { ToastType } from "@/enums";
/** utilities */
import { checkForJinja } from "@/components/SchemaEditor/utils";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { ensureDefaultApiType } from "@/utils/prompt-blueprint";
import { displayToast } from "@/utils/toast";
import {
  getBuiltInToolsForProvider,
  getSupportedBuiltInToolTypes,
  normalizeBuiltInToolProvider,
  type GetBuiltInToolsOptions,
} from "@/components/FunctionsModal/built-in-tools";
import { getMajorityFormat, isBuiltInTool, isMcpTool, isToolVariable } from "@/utils/utils";
import { FALLBACK_PLAYGROUND_PROVIDER_SLUG } from "@/components/Playground/ProviderApiKeyPrompt/constants";
import type { PendingChatRetryAfterApiKey } from "@/components/Playground/ProviderApiKeyPrompt/pending-chat-retry-types";
import {
  DashboardProviderApiKeyErrorCode,
  isDashboardApiKeyHttpError,
} from "@/types/dashboard-api-key-api-error";

const DEFAULT_INPUT_VARIABLE_SET_NAME = "Input Variables";
const CANCEL_PLAYGROUND_RUN_ERROR =
  "Unable to stop the backend run. It may continue in the background.";

export class PlaygroundStore {
  private _mode: PlaygroundType = PLAYGROUND_TYPES.CHAT;
  @observable private _messages: IMessageStore[] = [
    new MessageStore({
      content: [DEFAULT_MESSAGE_1],
      role: DEFAULT_MESSAGE_1_ROLE,
    }),
    new MessageStore({
      content: [DEFAULT_MESSAGE_2],
      role: DEFAULT_MESSAGE_2_ROLE,
    }),
  ];
  private _previous_messages: MessageStore[] = [
    new MessageStore({
      content: [DEFAULT_MESSAGE_1],
      role: DEFAULT_MESSAGE_1_ROLE,
    }),
    new MessageStore({
      content: [DEFAULT_MESSAGE_2],
      role: DEFAULT_MESSAGE_2_ROLE,
    }),
  ];
  @observable private _input_variables: Variable[] = [];
  @observable private _input_variable_sets: VariableSet[] = [];
  @observable private _previous_input_variable_sets: VariableSet[] = [];
  @observable private _detected_input_variables: string[] = [];
  @observable private _is_detecting_input_variables = true;
  @observable private _editing_variable_set_id: string | null = null;
  @observable private _active_input_variable_set_id: string | null = null;
  private _input_variable_set_name_counter = 1;
  @observable private _is_ai_prompt_assistant_loading = false;
  private _model: Model | null = null;
  private _metadata: Metadata | null = null;
  private _streamingRequiredByModel = false;
  private _streamingDisallowedByModel = false;
  private _provider_id: number | undefined = undefined;
  private _template_format: TemplateFormat = "jinja2";
  @observable private _hasFormatMismatch = false;
  private _perMessageFormats = new Map<number, TemplateFormat>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _functions: any = {};
  private _run_group_error: string | null = null;
  @observable private _batch_provider_api_key_prompt: {
    provider: string;
    recaptchaResponse: string;
  } | null = null;
  @observable private _chat_provider_api_key_prompt: { provider: string } | null = null;
  @observable private _pending_chat_retry_after_api_key: PendingChatRetryAfterApiKey | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _structured_output: any = {};

  /**
   * Chat session
   */
  @observable private _drawer_mode: PlaygroundDrawerModeType = PLAYGROUND_DRAWER_MODES.OUTPUT;
  private _session_mode: PlaygroundSessionModeType = PLAYGROUND_SESSION_MODES.BATCH;
  private _chat_settings: Partial<ChatSettingsI> | null = {};
  private _chat_store: ChatStore = new ChatStore();
  // Mocked agent runner: chat sends answer every tool call with an LLM-generated mock.
  private _mock_tool_responses = false;
  private _mock_scenario = "";
  // Comma-separated tool names that halt the mock loop when the agent calls one.
  private _mock_stop_tools = "";

  private _workspace_id: number;
  private _latestIndividualRunRequestId = -1;
  private _runs: IndividualRun[] = [];
  private _inference_client_name: string | null = null;
  private _provider_base_url_name: string | null = null;

  private _outputs: IPlayground[] = [];
  private _initial_outputs: IPlayground[] = [];
  private _initial_outputs_page = 1;
  private userToken: string;
  private _error: string | null = null;
  private _instanceId: string = v4();
  private _tools?: Tool[] = [];
  // Map of tool_registry_id -> resolved FunctionTool definition, used for playground execution
  private _registryToolDefinitions = new Map<number, FunctionTool>();
  private _tool_choice: ToolChoice | undefined = undefined;
  private _function_call: ChatFunctionCall | undefined = undefined;
  private _selected_template_verison?: PromptVersion = undefined;
  private _operation_store: OperationStore = new OperationStore();
  private _current_operation: Operation | null = null;
  private _run_details_fetched = false;
  private _initial_payload: string | null = null;
  private _is_pending_session_initialization = false;
  private _session_name: string | null = null;
  private _initializing = false;
  private _is_saving_session = false;
  private _session_updated_at: string | null = null;
  private _readonly = false;
  private _editor = false;
  private _is_inline_editor = false;
  private _is_snippet = false;
  private _is_xray_enabled = false;
  private _xray_blueprint: PromptBlueprint | null = null;
  private _request_log_ids?: number[];
  private _source?: PlaygroundSessionSource;
  private _saveTimeoutId?: NodeJS.Timeout;
  private _waitForSessionSaveCompletion: (() => Promise<void>) | null = null;
  public snippetOverrideStore = new SnippetOverrideStore();

  /** Buffer for WS messages that arrive before REST assigns request_ids */
  @observable private _messageBuffer = new Map<number, { eventName: string; payload: unknown }[]>();
  private _wsHandlers = new Map<string, (payload: unknown) => void>();
  private _pendingFlushIds = new Set<number>();

  registerWsHandler(eventName: string, handler: (payload: unknown) => void) {
    this._wsHandlers.set(eventName, handler);
    // Retry any pending flushes that may have been waiting for this handler
    if (this._pendingFlushIds.size > 0) {
      const ids = [...this._pendingFlushIds];
      for (const id of ids) {
        this.flushBuffer(id);
      }
    }
  }

  unregisterWsHandler(eventName: string) {
    this._wsHandlers.delete(eventName);
  }

  @action bufferMessage(requestId: number, eventName: string, payload: unknown) {
    const existing = this._messageBuffer.get(requestId) ?? [];
    existing.push({ eventName, payload });
    this._messageBuffer.set(requestId, existing);
  }

  @action flushBuffer(requestId: number) {
    const messages = this._messageBuffer.get(requestId);
    if (!messages) {
      this._pendingFlushIds.delete(requestId);
      return;
    }
    // Replay messages that have handlers, keep those that don't
    const remaining: { eventName: string; payload: unknown }[] = [];
    for (const entry of messages) {
      const handler = this._wsHandlers.get(entry.eventName);
      if (handler) {
        handler(entry.payload);
      } else {
        remaining.push(entry);
      }
    }
    if (remaining.length > 0) {
      this._messageBuffer.set(requestId, remaining);
      this._pendingFlushIds.add(requestId);
    } else {
      this._messageBuffer.delete(requestId);
      this._pendingFlushIds.delete(requestId);
    }
  }

  @action clearBufferForOperation(operationId: string) {
    const operation = this._operation_store.getOperation(operationId);
    if (!operation) {
      return;
    }
    for (const set of Object.values(operation.sets)) {
      if (set.request_id) {
        this._messageBuffer.delete(set.request_id);
        this._pendingFlushIds.delete(set.request_id);
      }
    }
  }

  constructor({
    workspace_id,
    prompt_blueprint,
    input_variables,
    input_variable_sets,
    userToken,
    selected_template_version,
    operation,
    instance_id,
    outputs,
    readonly,
    override_id,
    editor,
    inline_editor,
    is_snippet,
    request_log_ids,
    snippet_overrides,
    source,
  }: {
    workspace_id: number;
    prompt_blueprint?: Partial<PromptBlueprint>;
    input_variables?: InputVariableRow[];
    input_variable_sets?: VariableSet[];
    userToken: string;
    selected_template_version?: PromptVersion;
    operation?: Operation;
    instance_id?: string;
    outputs?: IPlayground[];
    readonly?: boolean;
    override_id?: string;
    editor?: boolean;
    inline_editor?: boolean;
    is_snippet?: boolean;
    request_log_ids?: number[];
    snippet_overrides?: Record<string, string>;
    source?: PlaygroundSessionSource;
  }) {
    if (instance_id) {
      this._instanceId = instance_id;
      this._is_pending_session_initialization = true;
    } else if (override_id) {
      this._instanceId = override_id;
      this._is_pending_session_initialization = true;
    }

    if (snippet_overrides) {
      this.snippetOverrideStore.setSnippetOverrides(snippet_overrides);
    }

    if (editor) {
      this._editor = editor;
    }

    if (inline_editor) {
      this._is_inline_editor = inline_editor;
    }

    if (is_snippet) {
      this._is_snippet = is_snippet;
    }

    if (outputs) {
      this._outputs = outputs;
    }

    if (readonly) {
      this._readonly = readonly;
    }

    if (!readonly && !prompt_blueprint?.metadata?.model) {
      this._model = DEFAULT_MODEL;
    }

    if (!prompt_blueprint?.metadata) {
      this._metadata = { markdown_enabled: true, streaming_enabled: true };
    }

    this._workspace_id = workspace_id;
    this.userToken = userToken;

    if (operation) {
      this._current_operation = operation;
    }
    if (prompt_blueprint) {
      // Ensure default API version for OpenAI providers
      const normalizedBlueprint = ensureDefaultApiType(prompt_blueprint);
      this.applyPromptBlueprintToState(normalizedBlueprint);
    }

    if (input_variable_sets) {
      this._input_variable_sets = input_variable_sets.map((set, index) => ({
        id: v4(),
        name:
          set.name || (index === 0 ? DEFAULT_INPUT_VARIABLE_SET_NAME : `Untitled set ${index + 1}`),
        variables: set.variables,
      }));
    }

    if (input_variables) {
      this._input_variable_sets = [
        {
          id: v4(),
          name: DEFAULT_INPUT_VARIABLE_SET_NAME,
          variables: input_variables.map(variable => ({
            ...variable,
            id: v4(),
          })),
        },
      ];
    }

    this._input_variable_set_name_counter = Math.max(this._input_variable_sets.length + 1, 2);
    this.ensureActiveInputVariableSet();

    if (selected_template_version) {
      this._selected_template_verison = selected_template_version;
    }
    if (request_log_ids) {
      this._request_log_ids = request_log_ids;
    }
    if (source) {
      this._source = source;
    }

    this._initial_payload = JSON.stringify(this.payload);
    makeAutoObservable(this, {}, { autoBind: true, deep: true });
  }

  get hasDefaultMessages() {
    const [firstMessage, secondMessage] = this.messages;
    if (!firstMessage) {
      return false;
    }
    const firstMessageMatches =
      "text" in DEFAULT_MESSAGE_1 && firstMessage.content === DEFAULT_MESSAGE_1.text;

    if (this.mode !== "completion") {
      return firstMessageMatches;
    }

    if (!secondMessage) {
      return false;
    }
    const secondMessageMatches =
      "text" in DEFAULT_MESSAGE_2 && secondMessage.content === DEFAULT_MESSAGE_2.text;

    return firstMessageMatches && secondMessageMatches;
  }

  get sessionName() {
    return this._session_name;
  }

  @action
  setSessionName(name: string) {
    this._session_name = name;
  }

  get isEditor() {
    return this._editor;
  }

  get isInlineEditor() {
    return this._is_inline_editor;
  }

  get isSnippet() {
    return this._is_snippet;
  }

  get isXRayMode() {
    return this._is_xray_enabled;
  }

  @action toggleXRayMode() {
    this._is_xray_enabled = !this._is_xray_enabled;
  }

  @action disableXRayMode() {
    this._is_xray_enabled = false;
    this._xray_blueprint = null;
  }

  @action setXRayBlueprint(blueprint: PromptBlueprint | null) {
    this._xray_blueprint = blueprint;
  }

  get isSavingSession() {
    return this._is_saving_session;
  }

  get sessionUpdatedAt() {
    return this._session_updated_at;
  }

  @action setSessionUpdatedAt(updatedAt: string | null) {
    this._session_updated_at = updatedAt;
  }

  @action
  setInitializing(initializing: boolean) {
    this._initializing = initializing;
  }

  get isInitializing() {
    return this._initializing;
  }

  get providerBaseUrlName() {
    return this._provider_base_url_name;
  }

  get isAIPromptAssistantLoading() {
    return this._is_ai_prompt_assistant_loading;
  }

  @action setIsAIPromptAssistantLoading(isAIPromptAssistantLoading: boolean) {
    this._is_ai_prompt_assistant_loading = isAIPromptAssistantLoading;
  }

  @action setIsSavingSession(isSavingSession: boolean) {
    // Clear any existing timeout
    if (this._saveTimeoutId) {
      clearTimeout(this._saveTimeoutId);
      this._saveTimeoutId = undefined;
    }

    this._is_saving_session = isSavingSession;

    // Safety mechanism: auto-reset save state if it gets stuck for more than 10 seconds
    if (isSavingSession) {
      this._saveTimeoutId = setTimeout(() => {
        if (this._is_saving_session) {
          // eslint-disable-next-line no-console
          console.warn("Save session state was stuck, auto-resetting");
          this._is_saving_session = false;
        }
        this._saveTimeoutId = undefined;
      }, 10000);
    }
  }

  @action setWaitForSessionSaveCompletion(handler: (() => Promise<void>) | null) {
    this._waitForSessionSaveCompletion = handler;
  }

  @action setDrawerMode(mode: PlaygroundDrawerModeType) {
    this._drawer_mode = mode;
  }

  @action setPlaygroundView(
    sessionMode: PlaygroundSessionModeType,
    drawerMode: PlaygroundDrawerModeType,
  ) {
    transaction(() => {
      this._session_mode = sessionMode;
      this._drawer_mode = drawerMode;
    });
  }

  waitForSessionSaveCompletion() {
    return this._waitForSessionSaveCompletion?.() ?? Promise.resolve();
  }

  get sourcedSnippets() {
    return this.selectedTemplateVersion?.sourced_snippets || [];
  }

  get workspace_id() {
    return this._workspace_id;
  }

  get drawerMode() {
    return this._drawer_mode;
  }

  get readonly() {
    return this._readonly;
  }

  get request_log_ids() {
    return this._request_log_ids || [];
  }

  get source() {
    return this._source;
  }

  @computed
  get functionType() {
    // Only return "functions" if functions are explicitly defined (legacy support)
    // Otherwise default to "tools" (modern API)
    if (this._functions && Object.keys(this._functions).length > 0) {
      return "functions";
    }
    return "tools";
  }

  @action setMetadata(metadata: Metadata) {
    const markdownEnabled = this._metadata?.markdown_enabled;
    this._metadata = {
      ...metadata,
      ...(markdownEnabled !== undefined && {
        markdown_enabled: markdownEnabled,
      }),
    };
  }

  @action
  initSession(session: PlaygroundSession) {
    // Ensure default API version for OpenAI providers
    const normalizedBlueprint = ensureDefaultApiType(session.prompt_blueprint);
    this.applyPromptBlueprintToState(normalizedBlueprint);

    // Preserve existing variable set IDs when possible to avoid breaking active dialogs
    const existingIds = this._input_variable_sets.map(set => set.id);
    this._input_variable_sets = session.input_variables.map((set, index) => ({
      id: existingIds[index] || v4(), // Use existing ID if available, otherwise generate new one
      name:
        session.input_variable_set_names?.[index] ||
        (index === 0 ? DEFAULT_INPUT_VARIABLE_SET_NAME : `Untitled set ${index + 1}`),
      variables: set,
    }));
    this._input_variable_set_name_counter = Math.max(this._input_variable_sets.length + 1, 2);
    this.ensureActiveInputVariableSet();
    this._session_name = session.name;
    this._session_updated_at = session.updated_at;

    // Hydrate snippet overrides
    if (session.snippet_overrides) {
      this.snippetOverrideStore.setSnippetOverrides(session.snippet_overrides);
    }

    if (session.mode) {
      if (session.mode === "chat") {
        this._session_mode = PLAYGROUND_SESSION_MODES.CHAT;
      } else if (session.mode === "evaluate") {
        this._session_mode = PLAYGROUND_SESSION_MODES.BATCH;
        this._drawer_mode = PLAYGROUND_DRAWER_MODES.EVALUATE;
      } else {
        // "output" or legacy "batch"
        this._session_mode = PLAYGROUND_SESSION_MODES.BATCH;
        this._drawer_mode = PLAYGROUND_DRAWER_MODES.OUTPUT;
      }
    }

    if (session.chat_settings) {
      this._chat_settings = session.chat_settings;
    }

    this._initial_payload = JSON.stringify(this.payload);
    this._is_pending_session_initialization = false;
  }

  get isPendingSessionInitialization() {
    return this._is_pending_session_initialization;
  }

  @action setIsPendingSessionInitialization(isPendingSessionInitialization: boolean) {
    this._is_pending_session_initialization = isPendingSessionInitialization;
  }

  get hasBeenEdited() {
    return (
      this._initial_payload !== JSON.stringify(this.payload) ||
      this.snippetOverrideStore.hasOverride
    );
  }

  @action setDetectedInputVariables(detected_input_variables: string[]) {
    this._detected_input_variables = detected_input_variables;
    this.ensureActiveInputVariableSet();
    this.syncDetectedInputVariablesToSets();
  }

  @action setIsDetectingInputVariables(isDetectingInputVariables: boolean) {
    this._is_detecting_input_variables = isDetectingInputVariables;
  }

  get isDetectingInputVariables() {
    return this._is_detecting_input_variables;
  }

  @computed
  get containsJinja2() {
    if (this.messages.some(m => checkForJinja(m.content))) {
      return true;
    }

    // Mirror the backend's extract_variables_from_dict: recurse through keys and
    // string values uniformly, no special-casing per JSON Schema field.
    const schemaContainsJinja = (node: unknown): boolean => {
      if (typeof node === "string") {
        return checkForJinja(node);
      }
      if (Array.isArray(node)) {
        return node.some(schemaContainsJinja);
      }
      if (node && typeof node === "object") {
        return Object.entries(node as Record<string, unknown>).some(
          ([key, value]) => checkForJinja(key) || schemaContainsJinja(value),
        );
      }
      return false;
    };

    const functionPropertiesContainJinja = (fn: Function_ | undefined): boolean => {
      if (!fn || !("parameters" in fn || "input_schema" in fn)) {
        return false;
      }
      const customFn = fn as CustomFunction_;
      const properties = (customFn.parameters ?? customFn.input_schema)?.properties;
      return schemaContainsJinja(properties);
    };

    const tools = this._tools ?? [];
    const activeRegistryIds = new Set(
      tools.filter((t): t is RegistryTool => t.type === "registry").map(t => t.tool_registry_id),
    );
    for (const [id, def] of this._registryToolDefinitions.entries()) {
      if (activeRegistryIds.has(id) && functionPropertiesContainJinja(def.function)) {
        return true;
      }
    }

    return tools.some(t => t.type === "function" && functionPropertiesContainJinja(t.function));
  }

  @computed
  get showJinjaWarning() {
    return this.template_format !== "jinja2" && this.containsJinja2;
  }

  @computed
  get hasParsingErrors() {
    return this.messages.some(message => message.parsingError);
  }

  @computed
  get messagesWithParsingErrors() {
    return this.messages.filter(message => message.parsingError);
  }

  @computed
  get parsingErrorSummary() {
    const messagesWithErrors = this.messagesWithParsingErrors;
    return {
      hasErrors: messagesWithErrors.length > 0,
      errorCount: messagesWithErrors.length,
      messages: messagesWithErrors.map(message => ({
        id: message.id,
        role: message.role,
        error: message.parsingError,
        content: message.content.substring(0, 100) + (message.content.length > 100 ? "..." : ""),
      })),
    };
  }

  get instanceId() {
    return this._instanceId;
  }

  get currentOperation() {
    return this._current_operation;
  }

  /** True once run details have been fetched (e.g. from individual-run-requests). Used to keep showing shimmer until UI can render. */
  get hasRunDetailsLoaded() {
    return (
      this._run_details_fetched ||
      Object.values(this._current_operation?.sets || {}).some(
        set => set?.run !== undefined && set?.run !== null,
      )
    );
  }

  get isGeneratingOutput() {
    const sets = Object.values(this._current_operation?.sets || {});
    return sets.some(set =>
      set.run?.status ? ["queued", "running"].includes(set.run.status) : !set.completed,
    );
  }

  get hasRenderableOutputMessages() {
    return this.outputMessages.length > 0;
  }

  @action markRunDetailsFetched() {
    this._run_details_fetched = true;
  }

  @action setCurrentOperation(operation: Operation) {
    this._current_operation = operation;
  }

  get operationStore() {
    return this._operation_store;
  }

  get chatStore() {
    return this._chat_store;
  }

  get error() {
    return this._error;
  }

  get selectedTemplateVersion() {
    return this._selected_template_verison;
  }

  @action setSelectedTemplateVersion(version: PromptVersion) {
    this._selected_template_verison = version;
  }

  get messagesContent() {
    return this.messages
      .map(
        message =>
          `${message.fullTextContent} ${
            message.media_variable
          } ${message.all_media_variables?.join(",")} ${message.selected_media?.map((_, index) => index)}`,
      )
      .join("\n");
  }

  get inputVariableSetsContent() {
    return JSON.stringify(this._input_variable_sets.map(v => v.variables));
  }

  reassignInitialPayload() {
    this._initial_payload = JSON.stringify(this.payload);
  }

  @action async run(recaptcha_response: string) {
    if (this.runGroupError) {
      this._run_group_error = null;
    }
    this.clearBatchProviderApiKeyPrompt();

    posthog.capture("Playground Run Start");
    if (this.outputsPage > 1) {
      this.setInitialOutputsPage(1);
    }
    let sets = this._input_variable_sets;
    if (sets.length === 0) {
      sets = [{ id: v4(), variables: [] }];
    }

    const id = this.operationStore.createNewOperation({
      input_variable_sets: toJS(sets),
    });

    try {
      await this.waitForSessionSaveCompletion();

      const runGroupPayload: RunGroupRequest = {
        id,
        playground_session_id: this.instanceId,
        shared_prompt_blueprint: this.blueprintWithSnippetOverrides,
        individual_run_requests: sets.map((set, index) => ({
          input_variables: set.variables.reduce(
            (acc, variable) => ({
              ...acc,
              [variable.key]: parseTypeOptimistically(variable.value),
            }),
            {},
          ),
          run_group_position: index + 1,
        })),
        stream: this.streamingEnabled,
        prompt_id: this.selectedTemplateVersion?.prompt_id ?? null,
        prompt_version_number: this.selectedTemplateVersion?.number ?? null,
      };

      const createNewRunGroup = await fetch(ENDPOINTS.run_groups(this._workspace_id), {
        method: "POST",
        headers: jsonAuthHeaders(this.userToken),
        body: JSON.stringify({
          ...runGroupPayload,
          recaptcha_response,
        }),
      });
      if (createNewRunGroup.status === 429) {
        throw new Error(
          "Error code: 429 - {'message': 'You have exceeded your free request limit. Please upgrade your plan to continue using the service without interruptions.'}",
        );
      }

      const runGroupData: RunGroupResponse = await handleResponse(createNewRunGroup);

      if (runGroupData.run_group && runGroupData.run_group.individual_run_requests) {
        runGroupData.run_group.individual_run_requests.forEach((request, index) => {
          const setId = sets[index]?.id;
          if (setId && request.id) {
            this.operationStore.assignSetRequestId(id, setId, request.id);
            this.operationStore.assignSetPosition(id, setId, request.run_group_position);
          }
        });
        // Replay any WS messages that arrived before request_ids were assigned
        runGroupData.run_group.individual_run_requests.forEach(request => {
          if (request.id) {
            this.flushBuffer(request.id);
          }
        });
      }

      if (this.operationStore.isCancelRequested(id)) {
        try {
          await this.cancelPlaygroundRuns(this.operationStore.getCancellationTargets([id]));
          return;
        } catch {
          this.operationStore.clearCancelRequested([id]);
          displayToast(CANCEL_PLAYGROUND_RUN_ERROR, ToastType.error);
        }
      }

      if (!runGroupData.success) {
        const errorMsg = "Whoops, something seems to have gone wrong. Please try again.";
        displayToast(errorMsg, ToastType.error);
        this.clearBufferForOperation(id);
        this.operationStore.deleteOperation(id);
        return;
      }

      if (runGroupData.warnings && runGroupData.warnings.length > 0) {
        runGroupData.warnings.forEach(warning => {
          displayToast(warning, ToastType.warning);
        });
      }
    } catch (error: unknown) {
      if (
        isDashboardApiKeyHttpError(error) &&
        error.error_code === DashboardProviderApiKeyErrorCode.PROVIDER_KEY_REQUIRED
      ) {
        this._batch_provider_api_key_prompt = {
          provider: error.provider ?? FALLBACK_PLAYGROUND_PROVIDER_SLUG,
          recaptchaResponse: recaptcha_response,
        };
        this.clearBufferForOperation(id);
        this.operationStore.deleteOperation(id);
        return;
      }
      if (typeof error === "string") {
        this._run_group_error = error;
      } else if (error instanceof Error) {
        this._run_group_error = error.message;
      } else {
        this._run_group_error = null;
      }
      this.clearBufferForOperation(id);
      this.operationStore.deleteOperation(id);
    }
  }

  get runGroupError() {
    return this._run_group_error;
  }

  get batchProviderApiKeyPrompt() {
    return this._batch_provider_api_key_prompt;
  }

  get chatProviderApiKeyPrompt() {
    return this._chat_provider_api_key_prompt;
  }

  get pendingChatRetryAfterApiKey() {
    return this._pending_chat_retry_after_api_key;
  }

  @action clearBatchProviderApiKeyPrompt() {
    this._batch_provider_api_key_prompt = null;
  }

  @action setChatProviderApiKeyGate(provider: string, pending: PendingChatRetryAfterApiKey) {
    this._chat_provider_api_key_prompt = { provider };
    this._pending_chat_retry_after_api_key = pending;
  }

  @action clearChatProviderApiKeyGate() {
    this._chat_provider_api_key_prompt = null;
    this._pending_chat_retry_after_api_key = null;
  }

  @action setRunGroupError(error: string | null) {
    this._run_group_error = error;
  }

  getMatchingBatchOperationSet(request_id: number) {
    if (!this._current_operation) {
      return;
    }
    const setList = Object.values(this._current_operation.sets);
    return setList.find(set => set.request_id === request_id);
  }

  @action setOperationRun(run: IndividualRun) {
    if (!this._current_operation) {
      return;
    }

    const matchingSet = this.getMatchingBatchOperationSet(run.id!);

    if (matchingSet) {
      this._current_operation.sets[matchingSet.id].run = run;
      const finalMessage = getFinalMessage(run);
      if (finalMessage) {
        if (matchingSet.message_id) {
          const existingMessage = this.getMessage(matchingSet.message_id);
          const finalContentBlocks = Array.isArray(finalMessage.content)
            ? finalMessage.content
            : [];
          // Don't replace a rendered websocket message with an empty reconstructed one.
          const wouldClearRenderedContent =
            existingMessage &&
            existingMessage.contentBlocks.length > 0 &&
            finalContentBlocks.length === 0;
          if (!wouldClearRenderedContent) {
            this.updateMessage(matchingSet.message_id, finalMessage as unknown as Message);
          }
        } else {
          // Non-streaming mode: no message was created yet, so add it
          const newId = this.addMessage(finalMessage as unknown as Message);
          this._current_operation.sets[matchingSet.id].message_id = newId;
        }
      }
    }
  }

  getMessageRun(message_id: string): IndividualRun | null {
    if (!this._current_operation) {
      return null;
    }
    return (
      Object.values(this._current_operation.sets).find(set => set?.message_id === message_id)
        ?.run || null
    );
  }

  getMessagePosition(message_id: string) {
    if (!this._current_operation) {
      return 0;
    }
    return (
      Object.values(this._current_operation.sets).find(set => set?.message_id === message_id)
        ?.position || 0
    );
  }

  @action setOperationMessageCompleted(request_id: number) {
    if (!this._current_operation) {
      return;
    }
    const matchingSetId = Object.values(this._current_operation.sets).find(
      set => set.request_id === request_id,
    )?.id;
    if (matchingSetId) {
      this._current_operation.sets[matchingSetId].completed = true;
    }
  }

  @action setRequestError(request_id: number, error: string) {
    if (!this._current_operation) {
      return;
    }
    const matchingSetId = Object.values(this._current_operation.sets).find(
      set => set.request_id === request_id,
    )?.id;
    if (matchingSetId) {
      this._current_operation.sets[matchingSetId].error = error;
    }
  }

  isOperationMessage(message_id: string) {
    if (!this._current_operation) {
      return false;
    }

    return Object.values(this._current_operation.sets).some(set => set?.message_id === message_id);
  }

  get pendingOutputs() {
    return Array.from(this.operationStore.getOperations().values()).filter(
      op => !op?.cancel_requested && !op?.output_instance_id,
    ).length;
  }

  get outputMessages() {
    return this._messages
      .filter(message => this.isOperationMessage(message.id))
      .sort((a, b) => this.getMessagePosition(a.id) - this.getMessagePosition(b.id));
  }

  isValidOperation(operation_id: string) {
    return !!this.operationStore.getOperation(operation_id);
  }

  isRequestValid(request_id: number) {
    // Used for filtering erroneous requests
    const isValid = this.operationStore.getAssociatedOperationByRequestId(request_id);
    return !!isValid;
  }

  isPendingBatchRequestInitialization(operation: Operation) {
    if (!operation) {
      return false;
    }

    return Object.values(operation.sets || {}).some(set => set.message_id);
  }

  get detectedInputVariables() {
    return this._detected_input_variables;
  }

  @computed
  get metadata() {
    return this._metadata ?? {};
  }

  @computed
  get toolChoice() {
    return this._tool_choice;
  }

  /**
   * When the provider changes, replace any built-in tools from the previous provider with the
   * new provider's equivalent (same type, e.g. web_search). If the new provider doesn't support
   * that type, the tool is removed.
   */
  private replaceBuiltInToolsForProvider(
    tools: Tool[],
    newProvider: string,
    options?: GetBuiltInToolsOptions,
  ): Tool[] {
    const providerLower = newProvider?.toLowerCase() as BuiltInToolProvider | undefined;
    if (!tools?.length || !providerLower) {
      return tools ?? [];
    }
    const normalizedProvider = normalizeBuiltInToolProvider(providerLower);
    const newProviderBuiltIns = getBuiltInToolsForProvider(providerLower, options);
    return tools
      .map((tool): Tool | null => {
        if (isMcpTool(tool)) {
          return tool;
        }
        if (!isBuiltInTool(tool as BuiltInTool)) {
          return tool;
        }
        const builtIn = tool as BuiltInTool;
        // For Vertex, always replace so config matches current model (Claude vs Gemini).
        const skipReplace =
          normalizeBuiltInToolProvider(builtIn.provider) === normalizedProvider &&
          providerLower !== "vertexai";
        if (skipReplace) {
          return tool;
        }
        // file_search configs are provider-specific (vector_store_ids vs
        // file_search_store_names), so drop rather than auto-translate across providers.
        if (builtIn.type === "file_search") {
          return null;
        }
        const replacement = newProviderBuiltIns.find(t => t.type === builtIn.type);
        // If no equivalent tool exists for the new provider, remove it.
        return replacement ? (replacement as Tool) : null;
      })
      .filter((tool): tool is Tool => {
        if (tool === null) {
          return false;
        }
        if (!isBuiltInTool(tool as BuiltInTool)) {
          return true;
        }
        return normalizeBuiltInToolProvider((tool as BuiltInTool).provider) === normalizedProvider;
      });
  }

  @action
  applyPromptBlueprintToState(
    prompt_blueprint: Partial<PromptBlueprint>,
    options?: { preserveSessionToggles?: boolean },
  ) {
    if (!prompt_blueprint) {
      return;
    }

    this._provider_base_url_name = prompt_blueprint.provider_base_url_name!;
    this._provider_id = prompt_blueprint.provider_id!;
    this._inference_client_name = prompt_blueprint.inference_client_name!;
    if (prompt_blueprint.metadata) {
      this._model = prompt_blueprint.metadata.model || DEFAULT_MODEL;
      const clone = { ...prompt_blueprint.metadata };
      delete clone.model;

      // ModelParameters snapshots can be stale; keep live Stream/Markdown toggle values.
      if (options?.preserveSessionToggles) {
        const markdownEnabled = this._metadata?.markdown_enabled;
        const streamingEnabled = this._metadata?.streaming_enabled;
        if (markdownEnabled !== undefined) {
          clone.markdown_enabled = markdownEnabled;
        }
        if (streamingEnabled !== undefined) {
          clone.streaming_enabled = streamingEnabled;
        }
      }

      this._metadata = clone;
    }

    // When only metadata is updated (e.g. user switched provider/model in the dropdown), replace
    // built-in tools from the previous provider with the new provider's equivalent (same type).
    const providerLower = this._model?.provider?.toLowerCase();
    if (this._tools?.length && providerLower) {
      this._tools = this.replaceBuiltInToolsForProvider(this._tools, providerLower, {
        modelName: this._model?.name,
        baseModel: this._model?.base_model ?? undefined,
      });
    }

    if (prompt_blueprint.prompt_template) {
      const template = prompt_blueprint.prompt_template;

      if ("tools" in template) {
        let tools = template.tools?.map((tool): Tool => {
          if (tool.type === "function" && tool.function) {
            return {
              type: "function" as const,
              function: tool.function,
            } as FunctionTool;
          }
          if (tool.type === "registry") {
            // Register resolved definition from backend enrichment
            const regTool = tool as RegistryTool & { _resolved_definition?: FunctionTool };
            if (regTool._resolved_definition && regTool.tool_registry_id) {
              this._registryToolDefinitions.set(
                regTool.tool_registry_id,
                regTool._resolved_definition,
              );
            }
            return tool as RegistryTool;
          }
          return tool as BuiltInTool;
        });
        // When applying a blueprint with tools, replace built-ins from other providers with the
        // current provider's equivalent.
        if (tools?.length && providerLower) {
          tools = this.replaceBuiltInToolsForProvider(tools, providerLower, {
            modelName: this._model?.name,
            baseModel: this._model?.base_model ?? undefined,
          });
        }
        this._tools = tools;
      }

      if ("tool_choice" in template) {
        this._tool_choice = template.tool_choice as ToolChoice;
      }

      if ("function_call" in template) {
        this._function_call = template.function_call as ChatFunctionCall;
      }

      if (template.type === "chat") {
        this._mode = "chat";

        // Detect per-message template_format mismatches (can happen when set via API/SDK)
        const formats = template.messages?.map(msg => msg.template_format || "f-string") ?? [];
        const uniqueFormats = new Set(formats);
        this._hasFormatMismatch = uniqueFormats.size > 1;
        this._perMessageFormats = new Map();

        if (this._hasFormatMismatch) {
          template.messages?.forEach((msg, idx) => {
            this._perMessageFormats.set(idx, (msg.template_format || "f-string") as TemplateFormat);
          });
        }

        this._template_format = getMajorityFormat(formats, "jinja2") as TemplateFormat;

        this._messages = template.messages.map(
          msg =>
            new MessageStore({
              ...msg,
            }),
        );
        if (template.functions) {
          this._functions = template.functions.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc: Record<string, any>, fn) => {
              // Only process CustomFunction_ types, not BuiltInTool types
              if ("parameters" in fn) {
                acc[fn.name] = {
                  name: fn.name,
                  description: fn.description,
                  parameters: fn.parameters,
                  strict: fn.strict,
                };
              }
              return acc;
            },
            {},
          );
        }
      } else {
        this._mode = "completion";
        this._template_format = template.template_format;
        this._messages = [
          new MessageStore({
            content: [
              {
                type: "text",
                text: template.content[0].type === "text" ? template.content[0].text : "",
              },
            ],
            role: "assistant",
          }),
        ];
      }

      if ("template_format" in template) {
        this._template_format = template.template_format as TemplateFormat;
      }
    }

    this.removeUnsupportedBuiltInTools();
  }

  get latestIndividualRun() {
    return this._runs?.[0];
  }

  get runs() {
    return this._runs;
  }

  @action
  addInputVariable(variable: Variable) {
    this._input_variables.push(variable);
  }

  @action
  updateInputVariable(set_id: string) {
    return action((id: string, key: string, value: string | object) => {
      const setIndex = this._input_variable_sets.findIndex(v => v.id === set_id);
      if (setIndex !== -1) {
        const variableIndex = this._input_variable_sets[setIndex].variables.findIndex(
          v => v.id === id,
        );
        if (variableIndex !== -1) {
          // Update existing variable
          this._input_variable_sets[setIndex].variables[variableIndex] = {
            ...this._input_variable_sets[setIndex].variables[variableIndex],
            key,
            value,
          };
        } else {
          // Add new variable
          this._input_variable_sets[setIndex].variables.push({
            id,
            key,
            value,
          });
        }
      }
    });
  }

  @action clearInputVariableSet(set_id: string) {
    const setIndex = this._input_variable_sets.findIndex(v => v.id === set_id);
    if (setIndex !== -1) {
      this._input_variable_sets[setIndex].variables = [];
    }
  }

  @action
  removeInputVariable(set_id: string) {
    return action((id: string) => {
      const set = this._input_variable_sets.find(s => s.id === set_id);
      if (set) {
        const variableIndex = set.variables.findIndex(v => v.id === id);
        if (variableIndex !== -1) {
          set.variables.splice(variableIndex, 1);
        }
      }
    });
  }

  get latestIndividualRunRequestId() {
    return this._latestIndividualRunRequestId;
  }

  @action setLatestIndividualRunRequestId(id: number) {
    this._latestIndividualRunRequestId = id;
  }

  @action addRun(run: IndividualRun) {
    this._runs.unshift(run);
  }

  get mode() {
    return this._mode;
  }

  set mode(value: PlaygroundType) {
    this._mode = value;
  }

  @action
  setMode(value: PlaygroundType) {
    if (this._mode === value) {
      return;
    }
    transaction(() => {
      const copied_messages = Array.from(this._messages);
      const copied_previous_messages = Array.from(this._previous_messages);
      this._previous_messages = copied_messages;
      this._messages = observable.array(copied_previous_messages);
      this._mode = value;
      const prevSetsCopy = Array.from(this._previous_input_variable_sets);
      this._previous_input_variable_sets = this._input_variable_sets;
      this._input_variable_sets = observable.array(prevSetsCopy || []);
    });
  }

  get sessionMode() {
    return this._session_mode;
  }

  set sessionMode(value: PlaygroundSessionModeType) {
    this._session_mode = value;
  }

  @action
  setSessionMode(value: PlaygroundSessionModeType) {
    this._session_mode = value;
  }

  get mockToolResponses() {
    return this._mock_tool_responses;
  }

  @action
  setMockToolResponses(enabled: boolean) {
    this._mock_tool_responses = enabled;
  }

  get mockScenario() {
    return this._mock_scenario;
  }

  @action
  setMockScenario(scenario: string) {
    this._mock_scenario = scenario;
  }

  get mockStopTools() {
    return this._mock_stop_tools;
  }

  @action
  setMockStopTools(tools: string) {
    this._mock_stop_tools = tools;
  }

  @computed
  get messages() {
    if (this.isXRayMode && this._xray_blueprint?.prompt_template) {
      const template = this._xray_blueprint.prompt_template;
      if (template.type === "chat") {
        return template.messages.map(msg => new MessageStore(msg));
      }
      return [
        new MessageStore({
          content: template.content,
          role: "assistant",
        }),
      ];
    }
    return this.isChat ? this._messages : this._messages.slice(0, 1);
  }

  @action
  addMessage(message: Message) {
    const newMessage = new MessageStore({
      ...message,
    });
    this._messages.push(newMessage);
    return newMessage.id;
  }

  @action
  addEmptyMessage() {
    transaction(() => {
      const newMessage = new MessageStore({
        content: [{ type: "text", text: "" }],
        role: DEFAULT_MESSAGE_TYPE,
      });
      this._messages.push(newMessage);
    });
  }

  @action
  updateMessage(message_id: string, value: Message) {
    const message = this._messages.find(msg => msg.id === message_id);
    if (message) {
      message.replace(value);
    }
  }

  @action
  appendToTemplate(message: string) {
    const completion_message: MessageStore = this._messages[0];
    completion_message.setMessage(completion_message.content + message);
  }

  deleteMessage(id: string) {
    if (this.currentOperation) {
      const relatedSet = Object.values(this.currentOperation.sets).find(
        set => set.message_id === id,
      );
      if (relatedSet) {
        delete this.currentOperation.sets[relatedSet.id];
      }
    }
    const index = this._messages.findIndex(message => message.id === id);
    if (index !== -1) {
      this._messages.splice(index, 1);
    }
  }

  /**
   * Reorders messages by moving a message from one position to another.
   * Used by the drag and drop functionality in the Messages component.
   *
   * @param startIndex - The current index of the message to move
   * @param endIndex - The target index where the message should be placed
   */
  @action
  reorderMessages(startIndex: number, endIndex: number) {
    const [reorderedMessage] = this._messages.splice(startIndex, 1);
    this._messages.splice(endIndex, 0, reorderedMessage);
  }

  @action
  setModel(model: Model) {
    this._model = model;
    // Replace built-in tools from a different provider with the new provider's equivalent.
    const providerLower = model.provider?.toLowerCase();
    if (this._tools?.length && providerLower) {
      this._tools = this.replaceBuiltInToolsForProvider(this._tools, providerLower, {
        modelName: model.name,
        baseModel: model.base_model ?? undefined,
      });
    }
  }

  @action updateJSONSchema(schema: SchemaDefinition | null) {
    if (!this._model || !this._model.parameters) {
      return;
    }

    if (!schema) {
      this._model.parameters.response_format = null;
      return;
    }

    this._model.parameters.response_format = {
      type: "json_schema",
      json_schema: schema,
    };
  }

  @action setInputVariableRows(values: InputVariableRow[]) {
    this._input_variables = values.map(row => ({
      id: v4(),
      key: row.key,
      value: row.value,
    }));
  }

  @action
  update(params: {
    model?: Model;
    inference_client_name: string | null;
    provider_base_url_name: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    functions: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tools: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tool_choice: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function_call: any;
  }): void {
    // Cache stringified values to avoid multiple stringifications
    const modelString = params.model ? JSON.stringify(params.model) : null;
    const currentModelString = this._model ? JSON.stringify(this._model) : null;
    const functionsString = JSON.stringify(params.functions);
    const currentFunctionsString = JSON.stringify(this._functions);
    const toolsString = JSON.stringify(params.tools);
    const currentToolsString = JSON.stringify(this._tools);
    const toolChoiceString = JSON.stringify(params.tool_choice);
    const currentToolChoiceString = this._tool_choice ? JSON.stringify(this._tool_choice) : null;
    const functionCallString = JSON.stringify(params.function_call);
    const currentFunctionCallString = JSON.stringify(this._function_call);

    // Batch updates in single transaction
    transaction(() => {
      if (params.tool_choice && toolChoiceString !== currentToolChoiceString) {
        this._tool_choice = params.tool_choice;
      }
      if (params.model && modelString !== currentModelString) {
        this._model = params.model;
      }

      if (this._inference_client_name !== params.inference_client_name) {
        this._inference_client_name = params.inference_client_name;
      }

      if (this._provider_base_url_name !== params.provider_base_url_name) {
        this._provider_base_url_name = params.provider_base_url_name;
      }

      if (functionsString !== currentFunctionsString) {
        this._functions = params.functions || {};
      }

      if (toolsString !== currentToolsString) {
        this._tools = params.tools;
      }

      if (functionCallString !== currentFunctionCallString) {
        this._function_call = params.function_call;
      }

      if (toolChoiceString !== currentToolChoiceString) {
        this._tool_choice = params.tool_choice;
      }
    });
    this.removeUnsupportedBuiltInTools();
  }

  get model() {
    return this._model;
  }

  get provider() {
    return this._model?.provider;
  }

  get model_name_and_model_config_display_name() {
    return {
      model_name: this._model?.name,
      model_config_display_name: this._model?.model_config_display_name,
    };
  }

  @action setProvider(provider: string) {
    if (this._model) {
      this._model.provider = provider;
    } else {
      this._model = {
        provider,
        name: "",
        parameters: {},
      };
    }
    // Replace built-in tools from a different provider with the new provider's equivalent.
    const providerLower = provider?.toLowerCase();
    if (this._tools?.length && providerLower) {
      this._tools = this.replaceBuiltInToolsForProvider(this._tools, providerLower, {
        modelName: this._model?.name,
        baseModel: this._model?.base_model ?? undefined,
      });
    }
  }

  get hasOutputContent() {
    return (
      (this.isPendingNewMessage && this.isChat) ||
      this.outputs.length > 0 ||
      this.pendingOutputs > 0
    );
  }

  @action setModelName(modelName: string) {
    if (this._model) {
      this._model.name = modelName;
    }
  }

  get isPendingNewMessage() {
    return this.pendingOutputs > 0;
  }

  get input_variables() {
    return this.input_variable_sets[0]?.variables || [];
  }

  get parameters() {
    return this._model?.parameters || {};
  }

  setParameter<K extends keyof ModelParameters>(key: K, value: ModelParameters[K]) {
    if (this._model) {
      this._model.parameters[key] = value;
    }
  }

  setParameters(parameters: ModelParameters) {
    if (this._model) {
      this._model.parameters = parameters;
    }
  }

  getParameterValue<K extends keyof ModelParameters>(key: K) {
    const value = this._model?.parameters[key];
    if (typeof value === "object") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (value as any).default;
    }
    return value;
  }

  get template_format() {
    return this._template_format;
  }

  set template_format(value: TemplateFormat) {
    this._applyGlobalFormat(value);
  }

  get hasFormatMismatch() {
    return this._hasFormatMismatch;
  }

  get perMessageFormats() {
    return this._perMessageFormats;
  }

  getMessageFormat(index: number): TemplateFormat {
    return this._perMessageFormats.get(index) ?? this._template_format;
  }

  @action
  setTemplateFormat(value: TemplateFormat) {
    this._applyGlobalFormat(value);
  }

  /** Apply a global format and clear any per-message mismatch state. */
  @action
  private _applyGlobalFormat(value: TemplateFormat) {
    this._template_format = value;
    this._hasFormatMismatch = false;
    this._perMessageFormats.clear();
  }

  get functions() {
    return this._functions
      ? Object.entries(this._functions).map(([key, value]) => ({
          name: key,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(value as any),
        }))
      : [];
  }

  get combinedToolsAndFunctions() {
    const functions = this._functions
      ? Object.entries(this._functions).map(([key, value]) => ({
          name: key,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(value as any),
        }))
      : [];
    const tools = this._tools
      ? this._tools
          .map(tool => {
            if ("function" in tool && tool.function) {
              return tool.function;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } else if (isBuiltInTool(tool as any)) {
              return tool as BuiltInTool;
            }
            return tool;
          })
          .filter(Boolean)
      : [];
    return [...tools, ...functions];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFunction(key: string, value: any) {
    this._functions[key] = value;
  }

  get structured_output() {
    return this._structured_output;
  }

  get isChat() {
    return this._mode === "chat";
  }

  get isSessionModeChat() {
    return (
      this.mode === PLAYGROUND_TYPES.CHAT && this.sessionMode === PLAYGROUND_SESSION_MODES.CHAT
    );
  }

  get playgroundSessionMode(): "chat" | "output" | "evaluate" {
    if (this.mode === PLAYGROUND_TYPES.CHAT && this.sessionMode === PLAYGROUND_SESSION_MODES.CHAT) {
      return "chat";
    }
    if (this.drawerMode === PLAYGROUND_DRAWER_MODES.EVALUATE) {
      return "evaluate";
    }
    return "output";
  }

  @computed({ keepAlive: true })
  get payload() {
    const chatProps = {
      mode: this.playgroundSessionMode,
      ...(this.isSessionModeChat ? { chat_settings: this.chat_settings } : {}),
    };

    return {
      input_variables: this._input_variable_sets.map(v => v.variables),
      input_variable_set_names: this.input_variable_set_names,
      prompt_blueprint: toJS(this.blueprint),
      workspace_id: this._workspace_id,
      request_log_ids: this._request_log_ids || [],
      ...chatProps,
    };
  }

  get prompt_template() {
    return {
      prompt_template: {
        prompt_name: Math.random().toString(36).substring(7),
        workspace_id: this._workspace_id,
      },
      prompt_version: this.blueprint,
    };
  }

  get markdownEnabled() {
    return this._metadata?.markdown_enabled ?? true;
  }

  @action
  toggleMarkdown() {
    if (!this._metadata) {
      this._metadata = { markdown_enabled: false };
    } else {
      this._metadata.markdown_enabled = !this.markdownEnabled;
    }
  }

  get streamingEnabled() {
    if (this._streamingRequiredByModel) {
      return true;
    }
    if (this._streamingDisallowedByModel) {
      return false;
    }
    return this._metadata?.streaming_enabled ?? true;
  }

  @action
  toggleStreaming() {
    if (this._streamingRequiredByModel || this._streamingDisallowedByModel) {
      return;
    }
    if (!this._metadata) {
      this._metadata = { streaming_enabled: false };
    } else {
      this._metadata.streaming_enabled = !this.streamingEnabled;
    }
  }

  get streamingRequiredByModel() {
    return this._streamingRequiredByModel;
  }

  @action
  setStreamingRequiredByModel(requiresStreaming: boolean) {
    this._streamingRequiredByModel = requiresStreaming;
  }

  get streamingDisallowedByModel() {
    return this._streamingDisallowedByModel;
  }

  @action
  setStreamingDisallowedByModel(disallowsStreaming: boolean) {
    this._streamingDisallowedByModel = disallowsStreaming;
  }

  hasMessage(id: string) {
    return this._messages.find(message => message.id === id);
  }

  get functionToolCallContent() {
    return `${JSON.stringify(this._tools)} ${JSON.stringify(this._functions)}`;
  }

  private buildBlueprintFromMessages(messages: IMessageStore[]): PromptBlueprint {
    const blueprint = {
      inference_client_name: this._is_snippet ? null : this._inference_client_name || null,
      metadata: {
        ...toJS(this._metadata),
        ...(this._is_snippet ? {} : { model: toJS(this._model!) }),
      },
      prompt_template:
        this._mode === "chat"
          ? {
              type: "chat" as const,
              messages: toJS(
                messages.map((message, idx) => ({
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ...(message.schema as any),
                  template_format: this._hasFormatMismatch
                    ? (this._perMessageFormats.get(idx) ?? this._template_format)
                    : this._template_format,
                })),
              ),
              tools:
                this._tools && this._tools.length > 0
                  ? toJS(
                      this._tools
                        .map(tool => {
                          if (isToolVariable(tool)) {
                            return tool;
                          }
                          if ("type" in tool && tool.type === "registry") {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const regTool = tool as any;
                            return {
                              type: "registry" as const,
                              tool_registry_id: regTool.tool_registry_id,
                              label: regTool.label ?? null,
                              version_number: regTool.version_number ?? null,
                              _resolved_name: regTool._resolved_name,
                            };
                          }
                          if (isMcpTool(tool)) {
                            return {
                              type: "mcp" as const,
                              mcp_server_id: tool.mcp_server_id,
                            };
                          }
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          if (isBuiltInTool(tool as any)) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            return tool as any as BuiltInTool;
                          } else if (tool.type === "function") {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const functionTool = tool as any;
                            if (functionTool.function && functionTool.function.name) {
                              return {
                                type: "function" as const,
                                function: {
                                  name: functionTool.function.name,
                                  description: functionTool.function.description || "",
                                  parameters: functionTool.function.parameters || {},
                                  strict: functionTool.function.strict,
                                  ...(functionTool.function.cache_control
                                    ? { cache_control: functionTool.function.cache_control }
                                    : {}),
                                },
                              };
                            }
                            return null;
                          }
                          return null;
                        })
                        .filter(tool => tool !== null),
                    )
                  : null,
              tool_choice: this._tool_choice || null,
              function_call: this._function_call || null,
              input_variables: [],
              functions:
                this._functions && Object.keys(this._functions).length > 0
                  ? toJS(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      Object.entries(this._functions).map(([name, value]: [string, any]) => ({
                        name: value.name || name,
                        description: value.description || "",
                        parameters: value.parameters || {},
                        strict: value.strict,
                      })),
                    )
                  : [],
            }
          : {
              type: "completion" as const,
              content: toJS(messages[0].rawContent),
              input_variables: [],
              template_format: this._template_format,
            },
      provider_base_url_name:
        this._is_snippet || this._provider_id ? null : this._provider_base_url_name,
      provider_id: this._is_snippet ? null : this._provider_id,
    } as PromptBlueprint;

    if (this._is_snippet) {
      return blueprint;
    }
    return ensureDefaultApiType(blueprint) as PromptBlueprint;
  }

  @computed({ keepAlive: true })
  get blueprint(): PromptBlueprint {
    return this.buildBlueprintFromMessages(this._messages);
  }

  @computed({ keepAlive: true })
  get blueprintWithSnippetOverrides(): PromptBlueprint {
    return this.buildBlueprintFromMessages(this.messagesOverride);
  }

  @action
  togglePlaygroundMode() {
    transaction(() => {
      const copied_messages = Array.from(this._messages);
      const copied_previous_messages = Array.from(this._previous_messages);
      this._previous_messages = copied_messages;
      this._messages = observable.array(copied_previous_messages);
      this._mode = this._mode === "chat" ? "completion" : "chat";
      this._model = this._mode === "chat" ? DEFAULT_CHAT_MODEL : DEFAULT_COMPLETION_MODEL;
      const prevSetsCopy = Array.from(this._previous_input_variable_sets);
      this._previous_input_variable_sets = this._input_variable_sets;
      this._input_variable_sets = observable.array(prevSetsCopy || []);
    });
  }

  @action
  toggleSessionMode() {
    this._session_mode =
      this._session_mode === PLAYGROUND_SESSION_MODES.CHAT
        ? PLAYGROUND_SESSION_MODES.BATCH
        : PLAYGROUND_SESSION_MODES.CHAT;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setStructuredOutput(value: any) {
    this._structured_output = value;
  }

  @action
  clone(operation?: Operation): PlaygroundStore {
    // TODO: debug lost parameter settings
    // Create deep clones of complex nested objects
    const clonedBlueprint = JSON.parse(JSON.stringify(toJS(this.blueprint)));
    const clonedVariables = JSON.parse(JSON.stringify(toJS(this.input_variable_sets)));

    return new PlaygroundStore({
      workspace_id: this._workspace_id,
      prompt_blueprint: clonedBlueprint,
      instance_id: operation?.output_instance_id || undefined,
      input_variable_sets: clonedVariables,
      userToken: this.userToken,
      operation: operation,
    });
  }

  get localOutputs() {
    return this._outputs;
  }

  get outputs() {
    return [
      ...this._outputs,
      ...this._initial_outputs.filter(
        elem =>
          !this._outputs.find(output => output.currentOperation?.id === elem.currentOperation?.id),
      ),
    ];
  }

  get isOutputCompleted() {
    return (
      this.currentOperation?.sets &&
      Object.values(this.currentOperation.sets).every(set => set.completed)
    );
  }

  get isMessageCompleted() {
    return (message_id: string) =>
      !!Object.values(this.currentOperation?.sets || {}).find(set => set.message_id === message_id)
        ?.completed;
  }

  get latestOutput() {
    return this._outputs[0];
  }

  @action deleteOutput(output: IPlayground) {
    const operationId = output.currentOperation?.id;
    const operation = operationId
      ? this.operationStore.getOperation(operationId)
      : this.operationStore.getAssociatedOperationByOutputId(output.instanceId);

    if (operation) {
      this.operationStore.deleteOperation(operation.id);
    }

    const isDeletedOutput = (item: IPlayground) =>
      operationId
        ? item.currentOperation?.id === operationId
        : item.instanceId === output.instanceId;

    this._outputs = this._outputs.filter(item => !isDeletedOutput(item));
    this._initial_outputs = this._initial_outputs.filter(item => !isDeletedOutput(item));

    // Avoid pagination issues when deleting the last item on a page
    if (this.outputs.length === 0 && this.outputsPage > 1) {
      this.setInitialOutputsPage(Math.max(1, this.outputsPage - 1));
    }
  }

  /** Clear a stuck Generating state when the completion websocket was missed. */
  @action reconcileMissedRunCompletion(runGroupId: string, freshOutput?: IPlayground): boolean {
    const operation = this.operationStore.getOperation(runGroupId);
    if (!operation) {
      return false;
    }

    if (operation.output_instance_id) {
      const index = this._outputs.findIndex(
        output => output.instanceId === operation.output_instance_id,
      );
      if (index !== -1) {
        const existingOutput = this._outputs[index];
        // Don't replace a renderable local output with a weaker polled snapshot.
        const wouldDowngrade =
          freshOutput &&
          existingOutput?.hasRenderableOutputMessages &&
          !freshOutput.hasRenderableOutputMessages;
        if (freshOutput && !wouldDowngrade) {
          this._outputs[index] = freshOutput;
        } else if (!freshOutput) {
          this._outputs.splice(index, 1);
        }
      } else if (freshOutput) {
        this._outputs.unshift(freshOutput);
      }
    }

    this.clearBufferForOperation(runGroupId);
    this.operationStore.deleteOperation(runGroupId);
    return true;
  }

  @action addOutput(output: IPlayground) {
    this._outputs.unshift(output);
    this._initial_outputs = [];
    this._initial_outputs_page = 1;
    return output;
  }

  @action setOutputs(outputs: IPlayground[]) {
    this._outputs = outputs;
  }

  get outputsPage() {
    return this._initial_outputs_page;
  }

  @action setInitialOutputsPage(page: number) {
    this._initial_outputs_page = page;
  }

  @action setInitialOutputs(outputs: IPlayground[]) {
    this._initial_outputs = outputs;
  }

  @action mergeInitialOutputsPage(outputs: IPlayground[], isFirstCursorPage: boolean) {
    const incomingOperationIds = new Set(outputs.map(output => output.currentOperation?.id));
    const existingWithoutIncoming = this._initial_outputs.filter(
      output => !incomingOperationIds.has(output.currentOperation?.id),
    );

    this._initial_outputs = isFirstCursorPage
      ? [...outputs, ...existingWithoutIncoming]
      : [...existingWithoutIncoming, ...outputs];
  }

  getOutput(output_id: string) {
    return this._outputs.find(output => output.instanceId === output_id);
  }

  get remainingBatchMessages() {
    return this.outputMessages.length;
  }

  get latestMessage() {
    return this.messages[this.messages.length - 1];
  }

  getMessage(message_id: string) {
    return this.messages.find(message => message.id === message_id);
  }

  getMessageByIndex(index: number) {
    return this.messages[index];
  }

  private async cancelPlaygroundRuns({
    runGroupIds,
    individualRunRequestIds,
  }: {
    runGroupIds: string[];
    individualRunRequestIds: number[];
  }) {
    if (runGroupIds.length === 0 && individualRunRequestIds.length === 0) {
      return;
    }

    const response = await fetch(ENDPOINTS.cancel_playground_runs(this._workspace_id), {
      method: "POST",
      headers: jsonAuthHeaders(this.userToken),
      body: JSON.stringify({
        run_group_ids: runGroupIds,
        individual_run_request_ids: individualRunRequestIds,
      }),
    });

    await handleResponse(response);
  }

  async cancel() {
    const operationIds = Array.from(this._operation_store.getOperations().keys());
    const cancellationTargets = this._operation_store.getCancellationTargets(operationIds);

    this._operation_store.markCancelRequested(operationIds);

    try {
      await this.cancelPlaygroundRuns(cancellationTargets);
      displayToast("Run stopped", ToastType.success);
    } catch {
      this._operation_store.clearCancelRequested(operationIds);
      displayToast(CANCEL_PLAYGROUND_RUN_ERROR, ToastType.error);
    }
  }

  @action popIn(output: IPlayground, message_id: string) {
    const lastOutputMessage = output.getMessage(message_id);
    if (!lastOutputMessage) {
      return;
    }

    this._messages.push(lastOutputMessage.clone());
  }

  @action rewind(output: IPlayground) {
    this._input_variable_sets = toJS(output._input_variable_sets);
    const outputCopy = output.clone();
    if (this.isChat) {
      output.outputMessages.forEach(() => outputCopy._messages.pop());
    } // Remove the output messages

    this.applyPromptBlueprintToState(outputCopy.blueprint);
  }

  @computed
  get tools(): (Function_ | BuiltInTool | ToolVariable | RegistryTool)[] {
    if (!this._tools) {
      return [];
    }
    const result: (Function_ | BuiltInTool | ToolVariable | RegistryTool)[] = [];
    for (const tool of this._tools) {
      if (isToolVariable(tool)) {
        result.push(tool);
      } else if ("type" in tool && tool.type === "registry") {
        result.push(tool as RegistryTool);
      } else if ("function" in tool && tool.function) {
        result.push(tool.function);
      } else if (isMcpTool(tool)) {
        result.push(tool as PromptLayerMcpTool);
      } else if (isBuiltInTool(tool as BuiltInTool)) {
        result.push(tool as BuiltInTool);
      }
    }
    return result;
  }

  @computed
  get hasImageGenerationTool(): boolean {
    return (
      this._tools?.some(
        (t): t is BuiltInTool => isBuiltInTool(t as BuiltInTool) && t.type === "image_generation",
      ) ?? false
    );
  }

  @computed
  get isGeminiImageModel(): boolean {
    const provider = this._model?.provider?.toLowerCase();
    if (provider !== "google" && provider !== "vertexai") {
      return false;
    }
    return this._model?.name?.toLowerCase().includes("image") ?? false;
  }

  /** True when image generation is active — via built-in tool (OpenAI), native Gemini image model, or Images API. */
  @computed
  get isImageGenerationMode(): boolean {
    if (this.isGeminiImageModel) {
      return true;
    }
    if (this._model?.api_type === "images") {
      return true;
    }
    const supported = getSupportedBuiltInToolTypes(this._model?.provider, this._model?.api_type, {
      modelName: this._model?.name,
      baseModel: this._model?.base_model ?? undefined,
    });
    return this.hasImageGenerationTool && supported.includes("image_generation");
  }

  /** Remove built-in tools that the current provider/API does not support. Call after provider or model changes. */
  @action
  removeUnsupportedBuiltInTools(): void {
    const provider = this._model?.provider;
    const supported = getSupportedBuiltInToolTypes(provider, this._model?.api_type, {
      modelName: this._model?.name,
      baseModel: this._model?.base_model ?? undefined,
    });
    if (!this._tools?.length) {
      return;
    }
    const filtered = this._tools.filter(t => {
      if (isMcpTool(t)) {
        return true;
      }
      if (!isBuiltInTool(t as BuiltInTool)) {
        return true;
      }
      const builtInTool = t as BuiltInTool;
      return supported.includes(builtInTool.type);
    });
    if (filtered.length !== this._tools.length) {
      this._tools = filtered;
    }
  }

  @computed
  get hasXRay() {
    return !!this._xray_blueprint;
  }

  @computed
  get functionCall() {
    return this._function_call;
  }

  get rewindBlueprint() {
    const outputCopy = this.clone();
    if (this.isChat) {
      this.outputMessages.forEach(() => outputCopy._messages.pop());
    } // Remove the output messages
    return outputCopy.blueprint;
  }

  //NOTE - Chat settings should be always based on the input variables
  //       only after the input variables are initialized
  @computed
  get chat_settings() {
    // Module not ready yet - return current settings without computing
    if (this._is_pending_session_initialization) {
      return this._chat_settings as ChatSettingsI | null;
    }

    // Module is ready - now compute based on available variables.
    // All variable sets share the same variable keys, so we validate
    // against the first set's keys.
    const variables = this._input_variable_sets[0]?.variables ?? [];

    //NOTE - return chart setting only if it exists in the input variables
    return {
      injection_variable:
        (variables.find(v => v.key === this._chat_settings?.injection_variable)?.key as string) ??
        null,
      user_input_variable:
        (variables.find(v => v.key === this._chat_settings?.user_input_variable)?.key as string) ??
        null,
    };
  }

  @action
  setChatSettings(chat_settings: Partial<ChatSettingsI>) {
    this._chat_settings = chat_settings;
  }

  /* Input Variable Sets */

  get input_variable_sets() {
    return this._input_variable_sets;
  }

  get activeInputVariableSetId() {
    this.ensureActiveInputVariableSet();
    return this._active_input_variable_set_id;
  }

  get activeInputVariableSet() {
    this.ensureActiveInputVariableSet();
    return (
      this._input_variable_sets.find(set => set.id === this._active_input_variable_set_id) ?? null
    );
  }

  @action
  setActiveInputVariableSetId(id: string | null) {
    this._active_input_variable_set_id = id;
    this.ensureActiveInputVariableSet();
  }

  private createDefaultInputVariableSet(): VariableSet {
    return {
      id: v4(),
      name: DEFAULT_INPUT_VARIABLE_SET_NAME,
      variables: this.detectedInputVariables.map(varName => ({
        id: v4(),
        key: varName,
        value: "",
      })),
    };
  }

  @action
  ensureActiveInputVariableSet() {
    if (this._input_variable_sets.length === 0) {
      const defaultSet = this.createDefaultInputVariableSet();
      this._input_variable_sets.push(defaultSet);
      this._active_input_variable_set_id = defaultSet.id;
      return;
    }

    const activeSetExists = this._input_variable_sets.some(
      set => set.id === this._active_input_variable_set_id,
    );
    if (!activeSetExists) {
      this._active_input_variable_set_id = this._input_variable_sets[0].id;
    }
  }

  @action
  private syncDetectedInputVariablesToSets() {
    this._input_variable_sets.forEach(set => {
      const existingKeys = new Set(set.variables.map(variable => variable.key));
      this.detectedInputVariables.forEach(varName => {
        if (!existingKeys.has(varName)) {
          set.variables.push({
            id: v4(),
            key: varName,
            value: "",
          });
        }
      });
    });
  }

  get raw_input_variable_sets() {
    return this._input_variable_sets.map(set => set.variables);
  }

  get input_variable_set_names() {
    return this._input_variable_sets.map(set => set.name ?? "");
  }

  private getNextInputVariableSetName() {
    const name = `Untitled set ${this._input_variable_set_name_counter}`;
    this._input_variable_set_name_counter += 1;
    return name;
  }

  @action addEmptyInputVariableSet(additionalVariableKeys: string[] = []): string {
    const variableKeys = Array.from(
      new Set([
        ...this.detectedInputVariables,
        ...this._input_variable_sets.flatMap(set => set.variables.map(variable => variable.key)),
        ...additionalVariableKeys,
      ]),
    ).filter(Boolean);
    const newInputVariableSet: VariableSet = {
      id: v4(),
      name: this.getNextInputVariableSetName(),
      variables: variableKeys.map(varName => ({
        id: v4(),
        key: varName,
        value: "",
      })),
    };
    this._input_variable_sets.push(newInputVariableSet);
    this._active_input_variable_set_id = newInputVariableSet.id;
    return newInputVariableSet.id;
  }
  @action
  addInputVariableSet(input_variable_set: VariableSet) {
    this._input_variable_sets.push(input_variable_set);
    this.ensureActiveInputVariableSet();
  }

  @action duplicateInputVariableSet(set: VariableSet) {
    const id = v4();
    const newInputVariableSet: VariableSet = {
      id,
      name: set.name ? `${set.name} (copy)` : this.getNextInputVariableSetName(),
      variables: toJS(set.variables),
    };
    this._input_variable_sets.push(newInputVariableSet);
    this._active_input_variable_set_id = id;
    return id;
  }

  @action
  updateInputVariableSet(id: string, field: "variables", newValue: Variable[]) {
    const input_variable_set = this._input_variable_sets.find(v => v.id === id);
    if (input_variable_set) {
      input_variable_set.variables = newValue;
    }
  }

  @action
  removeInputVariableSet(id: string) {
    if (this._input_variable_sets.length <= 1) {
      return;
    }

    const index = this._input_variable_sets.findIndex(v => v.id === id);
    if (index !== -1) {
      this._input_variable_sets.splice(index, 1);
    }
    this.ensureActiveInputVariableSet();
  }

  @action
  updateInputVariableSetName(id: string, name: string) {
    const set = this._input_variable_sets.find(v => v.id === id);
    if (set) {
      set.name = name;
    }
  }

  @action
  startEditingVariableSet(setId: string) {
    this._editing_variable_set_id = setId;
  }

  @action
  stopEditingVariableSet() {
    this._editing_variable_set_id = null;
  }

  get editingVariableSetId() {
    return this._editing_variable_set_id;
  }

  get isEditingVariableSet() {
    return this._editing_variable_set_id !== null;
  }

  @action
  updateFunctions(functions?: Function_[]) {
    this._functions = functions;
  }

  @action
  updateFunctionCall(function_call?: ChatFunctionCall) {
    this._function_call = function_call;
  }

  @action
  updateToolChoice(tool_choice?: ToolChoice) {
    this._tool_choice = tool_choice;
  }

  @action
  updateTools(tools?: Tool[]) {
    this._tools = tools;
  }

  @action
  registerToolDefinition(toolRegistryId: number, definition: FunctionTool) {
    this._registryToolDefinitions.set(toolRegistryId, definition);
  }

  getRegistryToolName(toolRegistryId: number): string | undefined {
    const def = this._registryToolDefinitions.get(toolRegistryId);
    if (def && "function" in def && def.function) {
      return (def.function as unknown as Record<string, unknown>).name as string;
    }
    return undefined;
  }

  get canAddInputVariableSet() {
    return this._input_variable_sets.length <= 4;
  }

  get canRemoveInputVariableSet() {
    return this._input_variable_sets.length > 1;
  }

  get messagesOverride() {
    return this.messages.map(m => {
      const message = m.toMessage();
      const overriddenContent = message.content.map(contentItem => {
        if (contentItem.type === "text") {
          return {
            ...contentItem,
            text: this.snippetOverrideStore.applySnippetOverrides(contentItem.text),
          };
        }
        return contentItem;
      });
      return new MessageStore({ ...message, content: overriddenContent });
    });
  }
}

export const PlaygroundStoreContext = createContext<PlaygroundStore | null>(null);

export const PlaygroundStoreProvider: React.FC<{
  children: React.ReactNode;
  value?: {
    prompt_blueprint?: Partial<PromptBlueprint>;
    input_variables?: InputVariableRow[];
    input_variable_sets?: VariableSet[];
    selected_template_version?: PromptVersion;
    readonly?: boolean;
    instance_id?: string;
    editor?: boolean;
    inline_editor?: boolean;
    is_snippet?: boolean;
    request_log_ids?: number[];
    snippet_overrides?: Record<string, string>;
    source?: PlaygroundSessionSource;
  };
}> = ({ children, value }) => {
  const { workspaceId, sessionId } = useParams();
  const userToken = useAuth()?.userToken || "";
  const store = useLocalObservable(
    () =>
      new PlaygroundStore({
        prompt_blueprint: value?.prompt_blueprint,
        input_variables: value?.input_variables,
        input_variable_sets: value?.input_variable_sets,
        workspace_id: Number(workspaceId),
        userToken,
        selected_template_version: value?.selected_template_version,
        instance_id: sessionId,
        override_id: value?.instance_id,
        readonly: value?.readonly,
        editor: value?.editor,
        inline_editor: value?.inline_editor,
        is_snippet: value?.is_snippet,
        request_log_ids: value?.request_log_ids,
        snippet_overrides: value?.snippet_overrides,
        source: value?.source,
      }),
  );

  return (
    <PlaygroundStoreContext.Provider value={store}>{children}</PlaygroundStoreContext.Provider>
  );
};

export const usePlayground = () => {
  const store = useContext(PlaygroundStoreContext);
  if (!store) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return store;
};

export const withPlaygroundState =
  <
    P extends {
      blueprint?: Partial<PromptBlueprint>;
      inputVariables?: InputVariableRow[];
      inputVariableSets?: VariableSet[];
      selectedTemplateVersion?: PromptVersion;
      readonly?: boolean;
      editor?: boolean;
      isSnippet?: boolean;
      instanceId?: string;
      request_log_ids?: number[];
      snippet_overrides?: Record<string, string>;
      source?: PlaygroundSessionSource;
      editorActions?: ReactNode;
    },
  >(
    Component: React.ComponentType<P>,
  ) =>
  (props: P) => {
    const {
      blueprint,
      inputVariables,
      inputVariableSets,
      selectedTemplateVersion,
      readonly,
      editor,
      isSnippet,
      instanceId,
      request_log_ids,
      snippet_overrides,
      source,
    } = props;
    return (
      <PlaygroundStoreProvider
        value={{
          prompt_blueprint: blueprint,
          input_variables: inputVariables,
          input_variable_sets: inputVariableSets,
          selected_template_version: selectedTemplateVersion,
          readonly,
          editor,
          is_snippet: isSnippet,
          instance_id: instanceId,
          request_log_ids,
          snippet_overrides,
          source,
        }}
      >
        <Component {...props} />
      </PlaygroundStoreProvider>
    );
  };

export const useSnippetOverrideStore = () => {
  try {
    const context = usePlayground();
    return context.snippetOverrideStore;
  } catch {
    // Return undefined if context is not available
    return undefined;
  }
};

export type IPlayground = InstanceType<typeof PlaygroundStore>;
